import * as Colors from '../helpers/Colors';
import styled from './styled';
import { TextInput } from './paper';
import { View, TouchableOpacity, StyleSheet, ScrollView } from '../web/react-native';
import React, { useRef, useState, useEffect } from 'react';
import { RichEditor, RichToolbar, actions, sanitizeHtml } from './RichEditor';

const Container = styled(View)`
  flex: 1;
  background-color: ${(props) => (props.disabled ? Colors.disabledBackground : '#fff')};
`;

const CustomRichEditor = styled(RichEditor)`
  flex: 1;
  padding: 10px;
  border-color: ${(props) => (props.disabled ? Colors.inactive : '#ccc')};
  border-width: 1px;
  background-color: ${(props) => (props.disabled ? Colors.disabledBackground : '#fff')};
`;

interface RichTextProps {
  disabled: boolean;
  value: string;
  onPressIn: any;
  right: any;
  onChangeText: any;
  autoFocus: boolean;
  placeholder: string;
  multiline: boolean;
  autoCorrect: boolean;
  mode: string;
  outlineColor: string;
  label: string;
  dense: boolean;
  theme: any;
  props: any;
}

export const RichTextField = ({
  disabled,
  value,
  onPressIn,
  right,
  onChangeText,
  autoFocus,
  placeholder,
  multiline = true,
  autoCorrect = false,
  mode = 'outlined',
  outlineColor = Colors.inactive,
  label = '',
  dense = true,
  theme = {
    colors: {
      primary: Colors.secondary,
      disabled: Colors.inactive,
      text: disabled ? Colors.disabledForeground : Colors.secondaryText,
    },
  },
  ...props
}: Partial<RichTextProps>) => {
  const richText = useRef(null);
  // `value` is stored HTML written by other users: sanitise it before it reaches the editor.
  const [content, setContent] = useState(() => sanitizeHtml(value));
  const editorHeight = 150; // Set height to 150px

  useEffect(() => {
    const safeValue = sanitizeHtml(value);
    if (safeValue !== content) {
      setContent(safeValue);
      if (richText.current) {
        richText.current.setContentHTML(safeValue);
      }
    }
  }, [value]);

  const handleChange = (html) => {
    const safeHtml = sanitizeHtml(html);
    setContent(safeHtml);
    if (onChangeText) {
      onChangeText(safeHtml);
    }
  };

  const handleFocus = () => {
    if (content === '' || content === placeholder) {
      setContent('');
      if (richText.current) {
        richText.current.setContentHTML('');
      }
    }
  };

  return (
    <Container disabled={disabled}>
      <TouchableOpacity onPress={onPressIn} disabled={disabled} activeOpacity={1}>
        <RichToolbar
          editor={richText}
          actions={[
            actions.setBold,
            actions.setItalic,
            actions.setUnderline,
            actions.heading1,
            actions.heading2,
            actions.alignLeft,
            actions.alignCenter,
            actions.alignRight,
            actions.alignFull,
            actions.insertBulletsList,
            actions.insertOrderedList,
            actions.insertLink,
          ]}
          disabled={disabled}
          iconTint={Colors.secondary}
          selectedIconTint={Colors.primary}
          disabledIconTint={Colors.inactive}
        />
        <ScrollView style={{ flex: 1 }}>
          <CustomRichEditor
            ref={richText}
            disabled={disabled}
            initialContentHTML={content || placeholder}
            editorInitializedCallback={() => {
              if (autoFocus) {
                richText.current?.focusContentEditor();
              }
            }}
            onChange={handleChange}
            onFocus={handleFocus}
            useContainer={false}
            containerStyle={{ minHeight: editorHeight }}
            style={styles.editor}
            {...props}
          />
        </ScrollView>
      </TouchableOpacity>
      {right}
    </Container>
  );
};

const styles = StyleSheet.create({
  editor: {
    flex: 1,
    padding: 10,
    borderColor: '#ccc',
    borderWidth: 1,
    backgroundColor: '#fff',
  },
});

export const TextField = styled(TextInput).attrs((props) => ({
  multiline: props.multiline ?? true,
  autoCorrect: false,
  scrollEnabled: false,
  mode: 'outlined',
  outlineColor: Colors.inactive,
  label: '',
  dense: true,
  theme: {
    colors: {
      primary: Colors.secondary,
      disabled: Colors.inactive,
      text: props.disabled ? Colors.disabledForeground : Colors.secondaryText,
    },
  },
}))`
  flex: 1;
  background-color: ${(props) => (props.disabled ? Colors.disabledBackground : '#fff')};
  padding-top: 0px;
`;
